import {
  AloftArea,
  AloftPrice,
  expected,
  isDefined,
  orUndefined,
  schAlertType,
  schAloftArea,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';

export const smallestSiteHasLowestValue: AlertDefinition = ({
  compSequence,
  propertyDataEntities,
  siteValues,
}) => {
  const compSites = compSequence
    .map((id, i) => ({
      id,
      name: `Comparable ${i + 1}`,
      size: orUndefined(schAloftArea.parse)(
        (propertyDataEntities[id] as CompPropertyDataState | undefined)?.compData.editableData.site
          .area,
      ),
      value: siteValues[id],
    }))
    .filter(
      (c): c is { id: string; name: string; size: AloftArea; value: AloftPrice } =>
        isDefined(c.size) && isDefined(c.value),
    );

  if (compSites.length < 2) return undefined;

  const minSiteValue = expected(AloftPrice.min(compSites.map((c) => c.value)));
  const minSiteArea = expected(AloftArea.min(compSites.map((c) => c.size)));

  const smallestSites = compSites.filter((c) => c.size?.equals(minSiteArea));

  // We allow a 5% tolerance on the min site value to account for minor variations
  const smallestSiteWithoutMinSiteValue = smallestSites.find((c) =>
    c.value?.gt(minSiteValue.mul(1.05)),
  );
  if (!smallestSiteWithoutMinSiteValue) return undefined;

  const smallestValueSite = expected(compSites.find((c) => c.value?.equals(minSiteValue)));

  return {
    type: schAlertType.enum.smallestSiteHasLowestValue,
    status: 'active',
    message: `The smallest Site Size (${smallestSiteWithoutMinSiteValue.name}: ${smallestSiteWithoutMinSiteValue.size?.toString()} | ${smallestSiteWithoutMinSiteValue.value?.toString()}) does not have the lowest Site Value (${smallestValueSite.name}: ${smallestValueSite.size?.toString()} | ${smallestValueSite.value?.toString()}) among the properties in the analysis.`,
    propertyID: smallestSiteWithoutMinSiteValue.id,
    fixingInfo: {
      tips: 'Tips: In a Sales Comparison with similar lots, smaller lots typically command lower prices. Provide clear justification if you believe the current entry is appropriate due to unique factors in the market (e.g., location, zoning, topography, access, utilities, demand).',
      pairedCostRouteItem: schPairedCostRouteItem.enum.analysis,
    },
  };
};
